import Dexie from 'dexie';

const DB_NAME = 'thinkmd_analytics';
const DB_VERSION = 1;
const MAX_TOTAL_EVENTS = 10000;
const CLEANUP_THRESHOLD_DAYS = 7;

class AnalyticsStorage {
  constructor() {
    this.db = null;
    this.isInitialized = false;
  }

  async init() {
    if (this.isInitialized) return;

    this.db = new Dexie(DB_NAME);

    this.db.version(DB_VERSION).stores({
      events_queue: '++id, uuid, userId, visitId, status, timestamp, [userId+status], [visitId+status]'
    });

    await this.db.open();
    this.isInitialized = true;
  }

  /**
   * Add event to queue
   * @param {Object} eventData - Event data
   * @returns {Promise<number>} Event ID
   */
  async addEvent(eventData) {
    await this.ensureInitialized();

    await this.enforceQuota();

    const event = {
      uuid: this.generateUUID(),
      userId: eventData.userId,
      visitId: eventData.visitId || null,
      deviceId: eventData.deviceId || this.getDeviceId(),
      sessionId: eventData.sessionId || null,
      event: eventData.event,
      properties: eventData.properties || {},
      timestamp: Date.now(),
      status: 'pending',
      retryCount: 0,
      lastSyncAttempt: null,
      errorMessage: null,
      createdAt: new Date().toISOString(),
      size: this.calculateSize(eventData)
    };

    const id = await this.db.events_queue.add(event);
    return id;
  }

  /**
   * Get batch of pending events for sync
   * @param {number} limit - Max events to fetch
   * @returns {Promise<Array>} Events array
   */
  async getBatchToSync(limit = 50) {
    await this.ensureInitialized();

    const events = await this.db.events_queue
      .where('status')
      .equals('pending')
      .limit(limit)
      .toArray();

    return events;
  }

  /**
   * Mark events as syncing
   * @param {Array<number>} eventIds - Event IDs
   */
  async markAsSyncing(eventIds) {
    await this.ensureInitialized();

    await this.db.events_queue
      .where('id')
      .anyOf(eventIds)
      .modify({
        status: 'syncing',
        lastSyncAttempt: Date.now()
      });
  }

  /**
   * Mark events as synced and delete them
   * @param {Array<number>} eventIds - Event IDs
   */
  async markAsSynced(eventIds) {
    await this.ensureInitialized();

    await this.db.events_queue.bulkDelete(eventIds);
  }

  /**
   * Mark events as failed
   * @param {Array<number>} eventIds - Event IDs
   */
  async markAsFailed(eventIds) {
    await this.ensureInitialized();

    await this.db.events_queue
      .where('id')
      .anyOf(eventIds)
      .modify(event => {
        event.status = event.retryCount >= 5 ? 'failed' : 'pending';
        event.retryCount += 1;
        event.lastSyncAttempt = Date.now();
      });
  }

  /**
   * Get storage statistics
   * @returns {Promise<Object>} Stats object
   */
  async getStats() {
    await this.ensureInitialized();

    const total = await this.db.events_queue.count();
    const pending = await this.db.events_queue.where('status').equals('pending').count();
    const syncing = await this.db.events_queue.where('status').equals('syncing').count();
    const failed = await this.db.events_queue.where('status').equals('failed').count();

    return { total, pending, syncing, failed };
  }

  /**
   * Enforce storage quota
   */
  async enforceQuota() {
    const total = await this.db.events_queue.count();

    if (total >= MAX_TOTAL_EVENTS) {
      const oldEvents = await this.db.events_queue
        .orderBy('timestamp')
        .limit(100)
        .toArray();

      await this.db.events_queue.bulkDelete(oldEvents.map(e => e.id));
    }
  }

  /**
   * Cleanup old synced events
   * @param {number} days - Delete events older than X days
   */
  async cleanupOldEvents(days = CLEANUP_THRESHOLD_DAYS) {
    await this.ensureInitialized();

    const cutoffTime = Date.now() - (days * 24 * 60 * 60 * 1000);
    
    await this.db.events_queue
      .where('timestamp')
      .below(cutoffTime)
      .delete();
  }

  /**
   * Clear all events (for testing/debugging)
   */
  async clearAll() {
    await this.ensureInitialized();
    await this.db.events_queue.clear();
  }

  // ===== Helper Methods =====

  async ensureInitialized() {
    if (!this.isInitialized) {
      await this.init();
    }
  }

  generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  getDeviceId() {
    const STORAGE_KEY = 'thinkmd_device_id';
    let deviceId = localStorage.getItem(STORAGE_KEY);

    if (!deviceId) {
      deviceId = 'device_' + this.generateUUID();
      localStorage.setItem(STORAGE_KEY, deviceId);
    }

    return deviceId;
  }

  calculateSize(data) {
    return JSON.stringify(data).length;
  }
}

// Export singleton instance
const analyticsStorage = new AnalyticsStorage();
export default analyticsStorage;
